import {announce} from '@react-aria/live-announcer'
import {FocusScope} from 'react-aria'

import CloseSvg from '@/assets/icons/close.svg?react'
import menuBar from '@/assets/icons/menu-bar.svg'
import PlusSvg from '@/assets/icons/plus.svg?react'
import logo from '@/assets/images/logo.svg'
import logoInvert from '@/assets/images/logo-invert.svg'
import ChatSection, {type ChatSectionControl} from '@/components/ChatSection/ChatSection'
import useMessages from '@/components/ChatSection/hooks/useMessages'
import {messageInputAtom, messageInputIsDisabledAtom} from '@/components/ChatSection/states'
import CartLink from '@/layout/CartLink'
import NavbarEnd from '@/layout/NavbarEnd'
import Sidebar from '@/layout/Sidebar'
import HeadTags from '@/lib/head/HeadTags'
import type {Thread} from '@/models/threadModel'
import {ChatRoute, HomeRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import DefaultMessage from '@/views/Chat/components/DefaultMessage/DefaultMessage'

import style from './Chat.module.scss'
import ChatThreads from './components/ChatThreads/ChatThreads'
import SkipLinks from './components/SkipLinks/SkipLinks'

export default memo(function Chat() {
  const isMobile = useIsMobile()
  const store = useStore()
  const navigate = useNavigate()
  const presetQ = ChatRoute.useSearch({
    select: search => search.q,
  })

  const chatSectionRef = useRef<ChatSectionControl>(null)

  const [sidebarIsShow, setSidebarIsShow] = useState(!isMobile)

  const clearMessageInput = useCallback(() => {
    store.set(messageInputAtom, '')
  }, [store])

  const disableMessageInput = useCallback(
    (isDisabled?: boolean) => {
      store.set(messageInputIsDisabledAtom, !!isDisabled)
    },
    [store],
  )

  const {submitMessage, isGenerating, stopGenerate, setActiveThreadId, activeThreadId} =
    useMessages({
      // After submitMessage successfully
      onStartGenerating: useCallback(() => {
        announce('One moment, Innosearch is generating the response...', 'assertive')
        clearMessageInput()
        disableMessageInput()
        chatSectionRef.current?.scrollToBottomChat()
      }, [disableMessageInput, clearMessageInput]),
      // After submitMessage done (isActive = false), or stop by user (isActive = true)
      onStopGenerating: useCallback(
        (isActive: boolean) => {
          disableMessageInput(false)
          if (!isActive) {
            chatSectionRef.current?.scrollToBottomChat()
          }
        },
        [disableMessageInput],
      ),
      // TODO: fix bug when send the first message of the thread, q got clear (because after sending the first message of the thread, message will be reload from remote server - by react-query)
      onStartLoadingMessages: useCallback(() => {
        clearMessageInput()
        disableMessageInput(false)
      }, [disableMessageInput, clearMessageInput]),
      onStopLoadingMessages: useCallback(() => {
        chatSectionRef.current?.scrollToBottomChat()
      }, []),
    })

  const startNewChat = useCallback(() => {
    if (isMobile) setSidebarIsShow(false)
    clearMessageInput()
    stopGenerate(Symbol('nothing'), true, 'new chat')
    setActiveThreadId(undefined)
    chatSectionRef.current?.focusToMessageInput()
  }, [clearMessageInput, isMobile, setActiveThreadId, stopGenerate])

  const selectThread = useCallback(
    (threadId: Thread['id']) => {
      if (isMobile) setSidebarIsShow(false)
      stopGenerate(Symbol('nothing'), true, 'change thread')
      // TODO: Prefetch
      setActiveThreadId(threadId)
      chatSectionRef.current?.focusToMessageInput()
    },
    [isMobile, setActiveThreadId, stopGenerate],
  )

  const focusToMessageInput = useCallback(() => {
    chatSectionRef.current?.focusToMessageInput()
  }, [])

  const qSent = useRef(false)

  useEffect(() => {
    if (presetQ && !qSent.current) {
      qSent.current = true
      startNewChat()
      // Wait for startNewChat operation to finish
      setTimeout(() => {
        submitMessage(presetQ)
      }, 0)
    }

    void navigate({
      to: ChatRoute.fullPath,
      replace: true,
    })
  }, [navigate, presetQ, startNewChat, submitMessage])

  useEffect(() => {
    chatSectionRef.current?.focusToMessageInput()
  }, [])

  return (
    <>
      {/* TODO: change title to active thread name */}
      <HeadTags title='Chat' />
      <SkipLinks />
      <div className={style.Chat}>
        <VisuallyHidden as='h1'>Chat Page</VisuallyHidden>
        {isMobile && (
          <div
            className={clsx(style.MenuBarOverlay, sidebarIsShow && style.Display)}
            onClick={() => {
              setSidebarIsShow(false)
            }}
            aria-hidden
          />
        )}
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- it's not html autoFocus, it's FocusScope's autoFocus */}
        <FocusScope contain={isMobile && sidebarIsShow} restoreFocus autoFocus>
          <nav
            className={clsx(style.SiderBarContainer, !sidebarIsShow && style.IsHidden)}
            aria-hidden={!sidebarIsShow}
            {...{
              inert: !sidebarIsShow ? true : undefined,
            }}
            {...skipTargetProps('navigation')}
          >
            <VisuallyHidden as='h2'>Navigation</VisuallyHidden>
            <div className={style.SiderBar}>
              <div className={style.Navbar}>
                <Link to={HomeRoute.fullPath} className={style.Logo} onClick={startNewChat}>
                  <img src={logoInvert} alt='Home' />
                </Link>
                {isMobile && (
                  <button
                    onClick={() => {
                      setSidebarIsShow(false)
                    }}
                    aria-label='Close sidebar'
                    className={style.CloseSidebarBtn}
                  >
                    <CloseSvg aria-hidden />
                  </button>
                )}
              </div>
              <button className={style.NewChat} onClick={startNewChat} aria-label='New chat'>
                <PlusSvg aria-hidden />
                <span aria-hidden>New Chat</span>
              </button>
              <ChatThreads onSelectThread={selectThread} activeThreadId={activeThreadId} />
            </div>
          </nav>
        </FocusScope>
        <div className={clsx(style.ChatView, !sidebarIsShow && style.IsFull)}>
          <header className={style.TopBar}>
            <div className={style.TopBarStart}>
              {isMobile && <Sidebar />}
              <button
                aria-label={'Sidebar'}
                className={style.ShowSidebar}
                onClick={() => {
                  setSidebarIsShow(state => !state)
                }}
                role='switch'
                aria-checked={sidebarIsShow}
              >
                <img src={menuBar} alt='' />
              </button>
              <button
                aria-label={'New chat'}
                className={clsx(style.NewChatMini, sidebarIsShow && style.IsHidden)}
                onClick={startNewChat}
                aria-hidden={!isMobile && sidebarIsShow}
                tabIndex={!isMobile && sidebarIsShow ? -1 : 0}
              >
                <PlusSvg />
              </button>
            </div>
            <button className={style.Logo} onClick={startNewChat}>
              <img src={logo} alt='InnoSearch' aria-hidden />
            </button>
            {!isMobile ? <NavbarEnd /> : <CartLink />}
          </header>
          <ChatSection
            ref={chatSectionRef}
            activeThreadId={activeThreadId}
            isGenerating={isGenerating}
            submitMessage={submitMessage}
            stopGenerate={stopGenerate}
          >
            <DefaultMessage
              submitMessage={submitMessage}
              focusToMessageInput={focusToMessageInput}
            />
          </ChatSection>
        </div>
      </div>
    </>
  )
})
